import { motion, AnimatePresence } from 'framer-motion';
import { useState, useMemo } from 'react';
import { X, Plus, Minus, Flame, Leaf } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const DEFAULT_CUSTOMISATION = {
  spice_levels: [
    { key: 'mild', label: 'Mild', price_delta: 0, icon: '🌿' },
    { key: 'medium', label: 'Medium', price_delta: 0, icon: '🌶️' },
    { key: 'spicy', label: 'Spicy', price_delta: 0, icon: '🔥' },
  ],
  portions: [
    { key: 'regular', label: 'Regular', price_delta: 0 },
    { key: 'large', label: 'Large', price_delta: 60 },
  ],
  addons: [
    { key: 'extra_rice', label: 'Extra Rice', price_delta: 40 },
    { key: 'extra_naan', label: 'Extra Butter Naan', price_delta: 49 },
    { key: 'raita', label: 'Raita', price_delta: 30 },
    { key: 'salad', label: 'Green Salad', price_delta: 25 },
  ],
};

export const CustomizeSheet = ({ item, open, onClose }) => {
  const { addItem } = useCart();

  // Merge item's config with defaults for backward compatibility
  const cfg = useMemo(() => {
    const c = item?.customisation_options || {};
    return {
      spice_levels: (c.spice_levels?.length ? c.spice_levels : DEFAULT_CUSTOMISATION.spice_levels),
      portions: (c.portions?.length ? c.portions : DEFAULT_CUSTOMISATION.portions),
      addons: (c.addons?.length ? c.addons : DEFAULT_CUSTOMISATION.addons),
    };
  }, [item]);

  const [spice, setSpice] = useState(() => cfg.spice_levels[Math.floor(cfg.spice_levels.length / 2)]?.key || 'medium');
  const [portion, setPortion] = useState(() => cfg.portions[0]?.key || 'regular');
  const [addons, setAddons] = useState([]);
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState('');

  if (!item) return null;

  const portionExtra = cfg.portions.find((p) => p.key === portion)?.price_delta || 0;
  const spiceExtra = cfg.spice_levels.find((s) => s.key === spice)?.price_delta || 0;
  const addonTotal = addons.reduce((s, a) => s + (cfg.addons.find((x) => x.key === a)?.price_delta || 0), 0);
  const unitPrice = item.price + portionExtra + spiceExtra + addonTotal;
  const total = unitPrice * qty;

  const toggleAddon = (key) => setAddons((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);

  const handleAdd = () => {
    const parts = [];
    const portionLabel = cfg.portions.find((p) => p.key === portion)?.label;
    if (portion !== cfg.portions[0]?.key && portionLabel) parts.push(portionLabel);
    const spiceLabel = cfg.spice_levels.find((s) => s.key === spice)?.label;
    if (spice !== cfg.spice_levels[Math.floor(cfg.spice_levels.length / 2)]?.key && spiceLabel) parts.push(spiceLabel);
    addons.forEach((k) => { const l = cfg.addons.find((a) => a.key === k)?.label; if (l) parts.push(l); });
    const custom = parts.join(', ');
    const customItem = {
      ...item,
      id: `${item.id}::${portion}::${spice}::${addons.sort().join(',')}`,
      name: custom ? `${item.name} (${custom})` : item.name,
      price: unitPrice,
      notes,
    };
    addItem(customItem, qty);
    toast.success(`${qty} × ${item.name} added`);
    onClose();
    setSpice(cfg.spice_levels[Math.floor(cfg.spice_levels.length / 2)]?.key || 'medium');
    setPortion(cfg.portions[0]?.key || 'regular');
    setAddons([]); setQty(1); setNotes('');
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-50"
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.4 }}
            onDragEnd={(_, info) => info.offset.y > 120 && onClose()}
            className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl shadow-warm max-h-[90vh] overflow-hidden flex flex-col"
            data-testid="customize-sheet"
          >
            <div className="mx-auto w-12 h-1.5 bg-borderbeige rounded-full mt-2 mb-1" />

            <div className="relative">
              <img src={item.image_url} alt={item.name} className="w-full h-40 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <button onClick={onClose} data-testid="customize-close" className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
              <div className="absolute bottom-3 left-4 right-4 text-white">
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-4 h-4 border-2 flex items-center justify-center ${item.is_veg ? 'border-fresh' : 'border-golden'} bg-white/90`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${item.is_veg ? 'bg-fresh' : 'bg-maroon'}`} />
                  </div>
                  <h2 className="font-heading font-bold text-lg">{item.name}</h2>
                </div>
                <p className="text-xs text-white/85 font-body line-clamp-1">{item.description}</p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {cfg.spice_levels.length > 0 && (
                <div>
                  <div className="flex items-center gap-1 mb-2">
                    <Flame className="w-4 h-4 text-saffron" />
                    <h3 className="font-heading font-bold text-sm">Spice Level</h3>
                  </div>
                  <div className={`grid gap-2`} style={{ gridTemplateColumns: `repeat(${Math.min(cfg.spice_levels.length, 4)}, 1fr)` }}>
                    {cfg.spice_levels.map((s) => (
                      <button key={s.key} onClick={() => setSpice(s.key)} data-testid={`spice-${s.key}`} className={`py-2.5 rounded-2xl border-2 transition-all font-heading font-semibold text-sm ${spice === s.key ? 'border-saffron bg-bgsecondary' : 'border-borderbeige'}`}>
                        {s.icon && <div className="text-lg">{s.icon}</div>}
                        <div>{s.label}</div>
                        {s.price_delta > 0 && <div className="text-[10px] text-textsecondary font-body">+₹{s.price_delta}</div>}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {cfg.portions.length > 0 && (
                <div>
                  <div className="flex items-center gap-1 mb-2">
                    <Leaf className="w-4 h-4 text-saffron" />
                    <h3 className="font-heading font-bold text-sm">Portion</h3>
                  </div>
                  <div className={`grid gap-2`} style={{ gridTemplateColumns: `repeat(${Math.min(cfg.portions.length, 3)}, 1fr)` }}>
                    {cfg.portions.map((p) => (
                      <button key={p.key} onClick={() => setPortion(p.key)} data-testid={`portion-${p.key}`} className={`p-3 rounded-2xl border-2 text-left ${portion === p.key ? 'border-saffron bg-bgsecondary' : 'border-borderbeige'}`}>
                        <div className="font-heading font-semibold text-sm">{p.label}</div>
                        <div className="text-xs text-textsecondary font-body">{p.price_delta > 0 ? `+₹${p.price_delta}` : 'Standard'}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {cfg.addons.length > 0 && (
                <div>
                  <h3 className="font-heading font-bold text-sm mb-2">Add-ons <span className="text-textlight font-body font-normal">(optional)</span></h3>
                  <div className="space-y-2">
                    {cfg.addons.map((a) => (
                      <label key={a.key} data-testid={`addon-${a.key}`} className="flex items-center justify-between p-3 rounded-xl border border-borderbeige cursor-pointer">
                        <div className="flex items-center gap-3">
                          <input type="checkbox" checked={addons.includes(a.key)} onChange={() => toggleAddon(a.key)} className="w-4 h-4 accent-saffron" />
                          <span className="font-body text-sm">{a.label}</span>
                        </div>
                        <span className="font-heading font-semibold text-maroon text-sm">+₹{a.price_delta}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h3 className="font-heading font-bold text-sm mb-2">Special Instructions</h3>
                <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. less oil, no onions..." data-testid="customize-notes" className="w-full px-3 py-2 border border-borderbeige rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-saffron/40" />
              </div>
            </div>

            <div className="border-t border-borderbeige p-4 flex items-center gap-3 bg-white">
              <div className="flex items-center gap-2 bg-bgsecondary rounded-full p-1">
                <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="customize-qty-dec" className="w-8 h-8 rounded-full bg-white flex items-center justify-center"><Minus className="w-4 h-4 text-saffron" /></button>
                <span className="font-heading font-bold min-w-[24px] text-center">{qty}</span>
                <button onClick={() => setQty(qty + 1)} data-testid="customize-qty-inc" className="w-8 h-8 rounded-full bg-saffron flex items-center justify-center"><Plus className="w-4 h-4 text-white" /></button>
              </div>
              <button onClick={handleAdd} data-testid="customize-add-btn" className="flex-1 bg-saffron text-white py-3 rounded-2xl font-heading font-bold shadow-warm hover:bg-saffron-dark">
                Add — ₹{total.toFixed(2)}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
